import { t } from "@lingui/macro";
import {
	DEFAULT_WHATSAPP_SETTINGS,
	type WhatsAppConversation,
	type WhatsAppConversationMessage,
	type WhatsAppThread,
} from "@repo/common";
import { db } from "@repo/ui";
import {
	collection,
	doc,
	limit,
	onSnapshot,
	orderBy,
	query,
} from "firebase/firestore";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { whatsAppDate } from "../hooks/useWhatsAppConversations";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

export function WhatsAppConversationDetails({
	conversation,
	onBack,
}: {
	conversation: WhatsAppConversation;
	onBack: () => void;
}) {
	const { user, companyProfile, language } = useData();
	const [now, setNow] = useState(Date.now);
	const [data, setData] = useState<{
		key: string;
		messages: WhatsAppConversationMessage[];
		thread: WhatsAppThread | null;
		error: boolean;
		loading: boolean;
		more: boolean;
	}>({
		key: "",
		messages: [],
		thread: null,
		error: false,
		loading: true,
		more: false,
	});
	const key = `${user?.companyId}:${conversation.id}`;
	const [pagination, setPagination] = useState({ key: "", size: 200 });
	const size = pagination.key === key ? pagination.size : 200;
	useEffect(() => {
		if (!user?.companyId || user.companyId !== conversation.companyId) return;
		let live = true;
		setData({
			key,
			messages: [],
			thread: null,
			error: false,
			loading: true,
			more: false,
		});
		const ref = doc(
			db,
			"companies",
			user.companyId,
			"whatsappConversations",
			conversation.id,
		);
		const stopMessages = onSnapshot(
			query(
				collection(ref, "whatsappConversationMessages"),
				orderBy("sequence", "desc"),
				limit(size + 1),
			),
			(snapshot) => {
				if (!live) return;
				const messages = snapshot.docs
					.slice(0, size)
					.map((item) => {
						const d = item.data();
						return {
							...d,
							id: item.id,
							providerAt: whatsAppDate(d.providerAt),
						} as WhatsAppConversationMessage;
					})
					.reverse();
				setData((old) => ({
					...old,
					key,
					messages,
					loading: false,
					more: snapshot.docs.length > size,
				}));
			},
			() => {
				if (live) setData((old) => ({ ...old, error: true, loading: false }));
			},
		);
		const stopThread = onSnapshot(
			doc(
				db,
				"companies",
				user.companyId,
				"whatsappThreads",
				conversation.threadId,
			),
			(snapshot) => {
				if (live)
					setData((old) => ({
						...old,
						thread: snapshot.exists()
							? (snapshot.data() as WhatsAppThread)
							: null,
					}));
			},
			() => {
				if (live) setData((old) => ({ ...old, error: true }));
			},
		);
		return () => {
			live = false;
			stopMessages();
			stopThread();
		};
	}, [
		key,
		user?.companyId,
		conversation.companyId,
		conversation.id,
		conversation.threadId,
		size,
	]);
	const current = data.key === key ? data : null;
	const config = companyProfile?.whatsappSettings || DEFAULT_WHATSAPP_SETTINGS;
	const thread = current?.thread,
		timeout = thread?.timeoutOverrideSeconds ?? config.humanTimeoutSeconds;
	const humanAt = thread?.lastHumanReplyAt
		? new Date(whatsAppDate(thread.lastHumanReplyAt)).getTime()
		: 0;
	const humanUntil = humanAt + timeout * 1000;
	const mode =
		thread?.mode === "human_temporary" && humanUntil <= now
			? "ai"
			: thread?.mode || "ai";
	const ended =
		conversation.status === "closed" ||
		new Date(conversation.lastMessageAt).getTime() +
			config.conversationInactivitySeconds * 1000 <=
			now;
	const inactivityEnd =
		new Date(conversation.lastMessageAt).getTime() +
		config.conversationInactivitySeconds * 1000;
	useEffect(() => {
		const future = [
			thread?.mode === "human_temporary" ? humanUntil : 0,
			inactivityEnd,
		].filter((value) => Number.isFinite(value) && value > now);
		if (!future.length) return;
		const timer = setTimeout(
			() => setNow(Date.now()),
			Math.min(2147483647, Math.max(1, Math.min(...future) - Date.now() + 10)),
		);
		return () => clearTimeout(timer);
	}, [humanUntil, inactivityEnd, thread?.mode, now]);
	const date = (s: string) =>
		new Date(s).toLocaleString(language === "he" ? "he-IL" : "en-US");
	if (user?.companyId !== conversation.companyId)
		return (
			<p role="status">
				{t({
					id: "whatsappConversation.companyChanged",
					message:
						"This conversation is not available in the selected company.",
				})}
			</p>
		);
	return (
		<div className="space-y-6" data-testid="whatsapp-conversation-details">
			<Button variant="ghost" onClick={onBack}>
				<ArrowLeft className="size-4 rtl:rotate-180" />
				{t({
					id: "whatsappConversation.back",
					message: "Back to calls and messages",
				})}
			</Button>
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div className="min-w-0 space-y-2">
					<h1 className="text-2xl font-semibold break-words">
						{conversation.customerName || `+${conversation.customerWaId}`}
					</h1>
					<p className="text-sm text-muted-foreground">
						<span dir="ltr">+{conversation.customerWaId}</span> · WhatsApp ·{" "}
						{date(conversation.startedAt)}
					</p>
				</div>
				<Badge variant="secondary">
					{ended
						? t({ id: "whatsappConversation.ended", message: "Ended" })
						: t({ id: "whatsappConversation.open", message: "Open" })}
				</Badge>
			</header>
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<MessageCircle className="size-4" />
						{t({
							id: "whatsappConversation.control",
							message: "Conversation control",
						})}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
					<div>
						<p className="text-muted-foreground">
							{t({ id: "whatsappConversation.mode", message: "Current mode" })}
						</p>
						<p className="font-medium">
							{mode === "human_persistent"
								? t({
										id: "whatsappConversation.humanPersistent",
										message: "Human · until resumed",
									})
								: mode === "human_temporary"
									? t({
											id: "whatsappConversation.humanTemporary",
											message: "Human · temporary",
										})
									: t({ id: "whatsappConversation.ai", message: "AI" })}
						</p>
					</div>
					<div>
						<p className="text-muted-foreground">
							{t({
								id: "whatsappConversation.timeout",
								message: "Human pause",
							})}
						</p>
						<p>
							{timeout / 3600}{" "}
							{t({ id: "whatsappConversation.hours", message: "hours" })} ·{" "}
							{thread?.timeoutOverrideSeconds != null
								? t({
										id: "whatsappConversation.override",
										message: "Conversation override",
									})
								: t({
										id: "whatsappConversation.companyDefault",
										message: "Company default",
									})}
						</p>
					</div>
					{mode === "human_temporary" && (
						<div>
							<p className="text-muted-foreground">
								{t({
									id: "whatsappConversation.eligible",
									message: "AI eligible after",
								})}
							</p>
							<p>{date(new Date(humanUntil).toISOString())}</p>
						</div>
					)}
					{thread?.pendingOutboundId && (
						<p role="status">
							{t({
								id: "whatsappConversation.pending",
								message: "AI response pending",
							})}
						</p>
					)}
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>
						{t({ id: "whatsappConversation.messages", message: "Messages" })}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{current?.error ? (
						<p role="alert">
							{t({
								id: "whatsappConversation.loadError",
								message:
									"Could not load this conversation. Go back and open it again to retry.",
							})}
						</p>
					) : current?.loading || !current ? (
						<p role="status">
							{t({
								id: "whatsappConversation.loading",
								message: "Loading conversation…",
							})}
						</p>
					) : (
						<>
							{current.more && (
								<p className="mb-4 text-sm text-muted-foreground">
									{t({
										id: "whatsappConversation.latestMessages",
										message: "Showing the latest messages.",
									})}
								</p>
							)}
							{current.more && (
								<Button
									variant="outline"
									className="mb-4"
									onClick={() => setPagination({ key, size: size + 200 })}
								>
									{t({
										id: "whatsappConversation.loadEarlier",
										message: "Load earlier messages",
									})}
								</Button>
							)}
							{current.messages.length === 0 && (
								<p>
									{t({
										id: "whatsappConversation.noMessages",
										message: "No messages are available yet.",
									})}
								</p>
							)}
							<ol
								className="divide-y"
								aria-label={t({
									id: "whatsappConversation.timeline",
									message: "Conversation timeline",
								})}
							>
								{current.messages.map((message) => (
									<li key={message.id} className="py-4 first:pt-0 last:pb-0">
										<div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
											<span className="font-medium text-foreground">
												{message.origin === "customer"
													? t({
															id: "whatsappConversation.customer",
															message: "Customer",
														})
													: message.origin === "ai"
														? t({
																id: "whatsappConversation.ai",
																message: "AI",
															})
														: t({
																id: "whatsappConversation.business",
																message: "Business team",
															})}
												{message.command ? ` · /${message.command.name}` : ""}
											</span>
											<time dateTime={message.providerAt}>
												{date(message.providerAt)}
											</time>
										</div>
										<p
											dir="auto"
											className="max-w-prose whitespace-pre-wrap break-words text-sm leading-relaxed"
										>
											{message.text ||
												t({
													id: "whatsappConversation.nonText",
													message: "Non-text message",
												})}
										</p>
										{message.origin === "ai" && (
											<p className="mt-2 text-xs text-muted-foreground">
												{message.status === "sent"
													? t({
															id: "whatsappConversation.sent",
															message: "Sent",
														})
													: message.status === "failed"
														? t({
																id: "whatsappConversation.failed",
																message: "Not sent",
															})
														: message.status === "needs_review"
															? t({
																	id: "whatsappConversation.review",
																	message: "Delivery needs review",
																})
															: t({
																	id: "whatsappConversation.queued",
																	message: "Queued",
																})}
											</p>
										)}
										{message.command?.name === "status" && (
											<p className="mt-2 text-xs text-muted-foreground">
												{t({
													id: "whatsappConversation.statusPrivate",
													message:
														"Status recorded here only; no status message was sent to the customer.",
												})}{" "}
												{message.command.mode === "human_persistent"
													? t({
															id: "whatsappConversation.humanPersistent",
															message: "Human · until resumed",
														})
													: message.command.mode === "human_temporary"
														? t({
																id: "whatsappConversation.humanTemporary",
																message: "Human · temporary",
															})
														: t({
																id: "whatsappConversation.ai",
																message: "AI",
															})}{" "}
												·{" "}
												{(message.command.effectiveTimeoutSeconds || 0) / 3600}{" "}
												{t({
													id: "whatsappConversation.hours",
													message: "hours",
												})}
											</p>
										)}
										{(message.command?.error || message.command?.ignored) && (
											<p className="mt-2 text-xs text-muted-foreground">
												{message.command.error
													? t({
															id: "whatsappConversation.invalidCommand",
															message:
																"Command was not applied: invalid timeout.",
														})
													: t({
															id: "whatsappConversation.delayedCommand",
															message:
																"Delayed command ignored because a newer control is already applied.",
														})}
											</p>
										)}
									</li>
								))}
							</ol>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
